import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiArrowRight } = FiIcons;

const NicknameEntry = ({ onJoin }) => {
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedNickname = nickname.trim();
    
    if (!trimmedNickname) {
      setError(t('nicknameRequired'));
      return;
    }

    if (trimmedNickname.length > 50) {
      setError('Nickname is too long (max 50 characters)');
      return;
    }

    if (trimmedNickname.length < 2) {
      setError('Nickname is too short (min 2 characters)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onJoin(trimmedNickname);
    } catch (error) {
      if (error.message === 'NICKNAME_TAKEN') {
        setError(t('nicknameTaken'));
      } else {
        setError('Failed to join chatroom. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiUser} className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">{t('enterChatroom')}</h1>
          </div>
          <LanguageSelector />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('nickname')}
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => {
                setNickname(e.target.value);
                setError('');
              }}
              placeholder={t('enterNickname')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              maxLength={50}
              minLength={2}
              disabled={isLoading}
            />
            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm mt-2"
              >
                {error}
              </motion.p>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={isLoading || !nickname.trim()}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {t('joining')}
              </>
            ) : (
              <>
                {t('join')}
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </>
            )}
          </motion.button>
        </form>

        <div className="mt-6 text-xs text-gray-500 text-center">
          <p>{t('nicknameRules')}</p>
        </div>
      </motion.div>
    </div>
  );
};

export default NicknameEntry;